import React, { useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Controller, useForm } from 'react-hook-form';
import PasswordChecklist from 'react-password-checklist';
import useFormPersist from 'react-hook-form-persist';
import ReCAPTCHA from 'react-google-recaptcha';
import { tldExists } from 'tldjs';
import { registrationPageOneValidationSchema } from '../../validation/registrationPageOneValidationSchema';
import SectionLoader from '../Loader/SectionLoader';
import FormContainer from '../FormContainer/FormContainer';
import { checkBasicUser } from '../../store/actions/login/loginActions';
import { useBrokerCode } from '../../util/hooks/useBrokerCode';
import { EMAIL_LOGIN_PAGE } from '../../constants/pages';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { CHECK_BASIC_USER_LOADING } from '../../store/actions/login/loginActionConstants';
import themeColors from '../../assets/styles/themeColors';
import {
  // selectIsReCaptchaEnabled,
  selectSettings,
} from '../../store/selectors/settingsSelectors';
import config from '../../config';
// import {
//   selectTokens,
//   selectTwoFactorAuthenticationResponse,
// } from '../../store/selectors/loginSelectors';
// import useTrackingDetails from '../../util/hooks/useTrackingDetails';
import { useQuery } from '../../util/hooks/useQuery';
// import { USER_LOGIN } from '../../constants/gtmEvents';
// import useGtmHook from '../../util/hooks/useGtmHook';
import ControlledInput from '../InputFields/ControlledInput';
import {
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../util/helpers/sessionStorageHelper';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import { postTrackingDetails } from '../../store/actions/affiliate/affiliateActions';
import {
  DISPATCH_TRACKING_DETAILS,
  LANDING_PAGE_URL,
  OFFER_CODE,
} from '../../constants/sessionStorage';
import {
  getAffParam,
  getCJeventParam,
  getPromotionParam,
} from '../../util/helpers/referrerHelper';
import { variables } from '../../assets/styles/variables';
import Anchor from '../Anchor/Anchor';
import Paragraph from '../Paragraph/Paragraph';
import { ReactComponent as CheckCircle } from '../../assets/images/svg/green_check.svg';
import { ReactComponent as EmptyCircle } from '../../assets/images/svg/grey_check.svg';

let trackingDetailsDispatched = false;

export const RegistrationWrapper = styled.div`
  input {
    &:focus {
      border-color: ${themeColors.colorRegistration};
    }
  }

  label {
    color: ${themeColors.colorTextPrimary};
    line-height: 2.1;
    font-weight: 600;
    font-size: ${variables.typography.p.normal.fontSize};
  }

  form {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
    max-width: 450px;
    width: 100%;
  }
`;

export const RegisterButton = styled.button`
  height: 48px;
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 12px 24px;
  border: none;
  outline: none;
  cursor: pointer;
  background-color: ${({ disabled }) =>
    disabled ? themeColors.colorButtonDisabled : themeColors.colorRegistration};
  width: 100%;
  justify-content: center;
  font-size: 16px;
  line-height: 1.6;
  color: ${themeColors.colorWhite};
  font-weight: 400;
`;
export const Error = styled.div`
  color: ${themeColors.colorError};
`;

export const RegistrationLoginLink = styled.a`
  color: ${themeColors.colorLinkRegistration};
  font-weight: 600;
  text-decoration: underline;
`;

export const BaseLayoutButtonContent = styled.div`
  display: ${(props) => (props.fullWidth ? 'grid' : 'flex')};
  grid-gap: ${(props) => (props.fullWidth ? '1rem' : 0)};
  align-items: center;
  margin-top: ${pxToRem(60)};

  @media only screen and (max-width: 992px) {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 1rem;
  }
`;

const PasswordChecklistWrapper = styled.div`
  color: ${themeColors.colorInfoText};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  background-color: ${themeColors.colorRegistrationInputBackground};
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  box-shadow: 0 5px 15px 0 rgba(0, 0, 0, 0.35);
  padding: 12px;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  margin-top: 8px;

  &::before {
    content: '';
    position: absolute;
    top: -9px;
    left: 24px;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 9px solid rgba(0, 0, 0, 0.2);
  }

  &::after {
    content: '';
    position: absolute;
    top: -8px;
    left: 24px;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid ${themeColors.colorRegistrationInputBackground};
  }

  ${mediaBelow(variables.breakpoints.bpXs)} {
    font-size: 13px;
  }

  span {
    color: ${themeColors.colorInfoText};
  }
`;

const GreyCircle = styled(EmptyCircle)`
  width: 22px;
  height: 22px;
  margin-right: 8px;
`;

const GreenCheck = styled(CheckCircle)`
  width: 22px;
  height: 22px;
  margin-right: 8px;
`;

export const MobileBottomBar = styled.div``;

const AlreadyHaveAccount = styled(Paragraph)`
  margin-top: 16px;
  font-size: 16px;

  ${mediaBelow(variables.breakpoints.bpLg)} {
    display: flex;
    justify-content: center;
  }
`;

const RegistrationPageOne = ({ stepNumber, updateStep }) => {
  const { t } = useTranslation();
  // const tokens = useSelector(selectTokens);
  const dispatch = useDispatch();
  const BrokerCode = useBrokerCode();
  const history = useHistory();
  const query = useQuery();
  const reCaptchaRef = React.useRef();
  const brokerSettings = useSelector(selectSettings);
  const {
    // IsBroker2FAActive,
    EnableGoogleReCaptchaForQuickRegistration,
  } = brokerSettings;
  const emailOnFocusRef = useRef('');
  const [errorCode, setErrorCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [hasError, setHasError] = useState(true);
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const referrerUrl = document.referrer;
  const landingPageUrl = window.location.href;
  const referrerAff = getAffParam();
  const referrerCJEvent = getCJeventParam();
  const referrerPromotionCode = getPromotionParam();
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const { RefereeEmail, email } = params;

  const {
    watch,
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(registrationPageOneValidationSchema),
  });
  useFormPersist('twostep1', { watch, setValue });

  const emailValue = watch('Email');
  const isTldValid = tldExists(emailValue || '');

  useEffect(() => {
    trigger();
  }, []); // eslint-disable-line

  // const twoFaAuthenticationResponse = useSelector(
  //   selectTwoFactorAuthenticationResponse,
  // );
  // const { TrackingDetails } = useTrackingDetails();
  // const { appGtmEvent } = useGtmHook();

  const isLoading = useSelector(
    selectIsLoadingByActionType(CHECK_BASIC_USER_LOADING),
  );

  useEffect(() => {
    const emailToFill = RefereeEmail || email;
    if (emailToFill) {
      setValue('Email', emailToFill);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    const trackingDetailsSent = retrieveFromSessionStorage(
      DISPATCH_TRACKING_DETAILS,
    );
    if (trackingDetailsSent) return;
    const { offer } = query;
    if (offer) {
      storeInSessionStorage(OFFER_CODE, offer);
    }
    storeInSessionStorage(LANDING_PAGE_URL, landingPageUrl);
    storeInSessionStorage(DISPATCH_TRACKING_DETAILS, true);
  }, [landingPageUrl, query]);

  useEffect(() => {
    if (trackingDetailsDispatched) {
      return;
    }

    const { aff, p, cjevent, a } = query;
    trackingDetailsDispatched = true;
    storeInSessionStorage(DISPATCH_TRACKING_DETAILS, 'SENT');
    dispatch(
      postTrackingDetails({
        data: {
          AffiliateCode: aff || referrerAff || a,
          CJEvent: cjevent || referrerCJEvent,
          PromotionCode: p || referrerPromotionCode,
          ReferrerUrl: referrerUrl,
        },
      }),
    );
  }, [
    dispatch,
    query,
    referrerAff,
    referrerCJEvent,
    referrerPromotionCode,
    referrerUrl,
  ]);

  /**
   * Only an existing username is remedied by editing the email, so only that
   * error hides on focus and clears on a change. A per-IP rate limit is not.
   */
  const isEmailError = errorCode === 'UsernameAlreadyExists';

  const handleEmailFocus = () => {
    setIsEmailFocused(true);
    emailOnFocusRef.current = emailValue || '';
  };

  const handleEmailBlur = () => {
    setIsEmailFocused(false);
    if (isEmailError && (emailValue || '') !== emailOnFocusRef.current) {
      setErrorCode('');
    }
  };

  const handlePwdFocus = () => {
    setIsPasswordFocused(true);
  };

  const handlePwdBlur = () => {
    setIsPasswordFocused(false);
  };

  const onSubmit = async (values) => {
    setHasError(true);
    setErrorCode('');
    /* ***************************************
     * REINSTATE for Google Recaptcha and ****
     * Tracking Details Data *****************
     * ______________BEGIN____________________
    let valueWithPhone = null;
    let token = null;
    if (EnableGoogleReCaptchaForQuickRegistration) {
      token = await reCaptchaRef.current.executeAsync();
      reCaptchaRef.current.reset();
    }
    const handleGtm = () => {
      // gtmEvent(APPLICATION_STARTED);
      appGtmEvent(`app.${USER_LOGIN}`);
    };
    const newData = token
      ? {
          BrokerCode,
          RecaptchaToken: token,
        }
      : {
          BrokerCode,
        };
    let NewTrackingDetails = TrackingDetails;

    if (ReferrerEmail) {
      NewTrackingDetails = {
        ...NewTrackingDetails,
        ReferrerEmail,
      };
    }
    * ___________________END___________________ */

    dispatch(
      checkBasicUser({
        userDetails: {
          ...values,
          BrokerCode,
        },
        setHasError,
        setErrorCode,
        setErrorMessage,
      }),
    );
  };

  useEffect(() => {
    if (!hasError) {
      updateStep(stepNumber);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError, updateStep]);

  /**
   * An existing username gets a link across to sign-in; every other code falls
   * through to the plain message so it is not silently dropped.
   */
  const displayError = () => {
    if (errorCode === '') {
      return;
    }
    if (isEmailError) {
      return (
        <Error>
          {errorMessage}
          <RegistrationLoginLink
            onClick={() => {
              history.push(EMAIL_LOGIN_PAGE);
            }}
          >
            {t('registration.signIn')}
          </RegistrationLoginLink>
          {t('registration.instead')}
        </Error>
      );
    }
    return <Error>{errorMessage}</Error>;
  };

  const handleExpired = () =>
    setErrorMessage(t('apiErrors.ExpiredRecaptchaToken'));

  const handleErrored = () =>
    setErrorMessage(t('apiErrors.InvalidRecaptchaToken'));

  return (
    <RegistrationWrapper>
      <SectionLoader isLoading={isLoading}>
        <FormContainer>
          <FormContainer hasError={errorCode !== ''}>
            <Controller
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState }) => (
                <ControlledInput
                  {...field}
                  onBlur={() => {
                    handleEmailBlur();
                    field.onBlur();
                  }}
                  error={
                    fieldState.isTouched && !isEmailFocused
                      ? fieldState.error
                      : undefined
                  }
                  type="email"
                  label={t('quickRegistration.labels.email')}
                  autoComplete="email"
                  data-cy="input-email"
                  note={
                    errorCode
                      ? ''
                      : t(
                          'quickRegistration.twoStep.rightPane.emailAddressFieldNote',
                        )
                  }
                  noteFontSize={14}
                  fontSize="14px"
                  isTwoStepRegistration
                  placeholder={t(
                    'quickRegistration.twoStep.rightPane.emailPlaceholder',
                  )}
                  handleFocus={handleEmailFocus}
                  height={54}
                />
              )}
              name="Email"
            />
            {(!isEmailFocused || !isEmailError) && displayError()}
          </FormContainer>
          <Controller
            control={control}
            render={({ field, fieldState }) => (
              <div style={{ position: 'relative' }}>
                <ControlledInput
                  {...field}
                  onBlur={() => {
                    handlePwdBlur();
                    field.onBlur();
                  }}
                  error={
                    fieldState.isTouched && !isPasswordFocused
                      ? fieldState.error
                      : undefined
                  }
                  type="password"
                  label={t('quickRegistration.labels.password')}
                  autoComplete="new-password"
                  fontSize="14px"
                  data-cy="input-password"
                  placeholder={t(
                    'quickRegistration.twoStep.rightPane.passwordPlaceholder',
                  )}
                  showEyeIcon
                  eyeIconMarginTop={12}
                  hideErrorOnFocus
                  handleFocus={handlePwdFocus}
                  height={54}
                />
                {isPasswordFocused && (
                  <PasswordChecklistWrapper>
                    <PasswordChecklist
                      rules={[
                        'minLength',
                        'capital',
                        'lowercase',
                        'number',
                        'specialChar',
                      ]}
                      minLength={8}
                      value={field?.value || ''}
                      messages={{
                        minLength: t(
                          `quickRegistration.twoStep.validationMessage.minimumLength`,
                        ),
                        capital: t(
                          `quickRegistration.twoStep.validationMessage.capital`,
                        ),
                        lowercase: t(
                          `quickRegistration.twoStep.validationMessage.lowercase`,
                        ),
                        number: t(
                          `quickRegistration.twoStep.validationMessage.number`,
                        ),
                        specialChar: t(
                          `quickRegistration.twoStep.validationMessage.specialChar`,
                        ),
                      }}
                      iconComponents={{
                        ValidIcon: <GreenCheck />,
                        InvalidIcon: <GreyCircle />,
                      }}
                    />
                  </PasswordChecklistWrapper>
                )}
              </div>
            )}
            name="Password"
          />
        </FormContainer>
        <MobileBottomBar>
          <BaseLayoutButtonContent fullWidth>
            <RegisterButton
              type="submit"
              data-cy="button-continue"
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid || !isTldValid}
            >
              {t('common.continue')}
            </RegisterButton>
          </BaseLayoutButtonContent>
          <AlreadyHaveAccount bold>
            {t(`quickRegistration.twoStep.rightPane.alreadyHaveAccount`)}
            &nbsp;
            <Anchor
              type="website"
              value={brokerSettings?.PortalUrl}
              text={t('quickRegistration.twoStep.rightPane.login')}
            />
          </AlreadyHaveAccount>

          {EnableGoogleReCaptchaForQuickRegistration && (
            <ReCAPTCHA
              ref={reCaptchaRef}
              sitekey={config.recaptcha.siteKey}
              size="invisible"
              onExpired={handleExpired}
              onErrored={handleErrored}
            />
          )}
        </MobileBottomBar>
      </SectionLoader>
    </RegistrationWrapper>
  );
};

RegistrationPageOne.propTypes = {
  history: PropTypes.shape({
    replace: PropTypes.func,
  }),
  stepNumber: PropTypes.number,
  updateStep: PropTypes.func,
};

export default RegistrationPageOne;
